import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Textarea } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  useInvitaciones, useInvitar, useRecordarPendientes,
  type Formulario, type Invitacion,
} from './api';
import { BuscadorDeEmpleado, fechaHora } from './piezas';

/**
 * Invitar y ver invitaciones: las dos caras del reparto de un formulario.
 *
 * ## Lo que cambia
 *
 * - **Los elegidos son fichas con su botón de quitar**, no `<span>` con una
 *   `<i>` pulsable dentro y el nombre metido en un `onclick`.
 * - **Las fechas van en la hora de quien mira.**
 * - **Una invitación vencida se cuenta aparte**, como ya hacía el vanilla: el
 *   enlace responde 410 y el recordatorio automático la salta, así que contarla
 *   como «sin responder» inflaba el seguimiento con trabajo que nadie puede
 *   hacer.
 */

export function Invitar({ formulario, onCerrar }: { formulario: Formulario; onCerrar: () => void }) {
  const invitar = useInvitar();
  const [elegidos, setElegidos] = useState<Array<{ id: number; nombre: string }>>([]);
  const [correos, setCorreos] = useState('');
  const [dias, setDias] = useState('14');
  const [enviarCorreo, setEnviarCorreo] = useState(true);
  const [error, setError] = useState('');
  const resultado = invitar.data;

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v && !invitar.isPending) onCerrar(); }}
      titulo={`Invitar a diligenciar: ${formulario.name}`}
      descripcion="Cada invitación lleva su propio enlace y su fecha de vencimiento."
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cerrar</Button>
          <Button
            loading={invitar.isPending}
            onClick={() => {
              if (!elegidos.length && !correos.trim()) {
                setError('Elige colaboradores o escribe al menos un correo.');
                return;
              }
              setError('');
              invitar.mutate(
                {
                  formId: formulario.id,
                  cuerpo: {
                    user_ids: elegidos.map((e) => e.id),
                    emails: correos.trim() || null,
                    expires_days: Number(dias) || 14,
                    send_email: enviarCorreo,
                  },
                },
                {
                  onSuccess: (r) => Utils.toast(
                    `${r.created} ${r.created === 1 ? 'invitación enviada' : 'invitaciones enviadas'}`,
                    'success'),
                  onError: (err) => setError(
                    err instanceof Error ? err.message : 'No se pudo invitar'),
                },
              );
            }}
          >
            <i className="bi bi-send" aria-hidden="true" /> Enviar invitaciones
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Colaboradores" opcional id="inv-empleado">
          <BuscadorDeEmpleado
            id="inv-empleado"
            etiqueta="Buscar colaborador por su nombre"
            alElegir={(e) => {
              setElegidos((xs) => (xs.some((x) => x.id === e.id)
                ? xs
                : [...xs, { id: e.id, nombre: e.label }]));
              setError('');
            }}
          />
        </Field>

        {!!elegidos.length && (
          <ul className="-mt-2 flex flex-wrap gap-1.5">
            {elegidos.map((e) => (
              <li key={e.id}>
                <span className="inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-1 font-semibold text-on-brand">
                  {e.nombre}
                  <button
                    type="button"
                    aria-label={`Quitar a ${e.nombre}`}
                    className="focus-visible:focus-anillo"
                    onClick={() => setElegidos((xs) => xs.filter((x) => x.id !== e.id))}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        <Field
          label="Nuevos o externos"
          opcional
          ayuda="Correos separados por coma. Quien no tiene cuenta responde con el enlace."
          error={error || undefined}
        >
          <Textarea
            rows={2}
            value={correos}
            onChange={(e) => { setCorreos(e.target.value); setError(''); }}
            placeholder="nuevo1@correo.com, nuevo2@correo.com"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Vence en (días)">
            <Input
              type="number" min={1} max={90}
              value={dias}
              onChange={(e) => setDias(e.target.value)}
            />
          </Field>
          <label className="flex items-end gap-2 pb-2.5 text-3 text-ink-2">
            <input
              type="checkbox"
              checked={enviarCorreo}
              onChange={(e) => setEnviarCorreo(e.target.checked)}
            />
            Enviar el enlace por correo
          </label>
        </div>

        {resultado && (
          <section className="rounded-sm border border-ok/40 bg-ok/12 p-3 text-2" aria-live="polite">
            <p className="text-ink-2">
              <strong>{resultado.created}</strong>{' '}
              {resultado.created === 1 ? 'invitación creada · vence' : 'invitaciones creadas · vencen'}{' '}
              el {resultado.expires}
            </p>
            <ul className="mt-2 max-h-40 overflow-auto">
              {resultado.links.map((l) => (
                <li key={l.to} className="flex items-center gap-2 border-b border-ok/20 py-1 last:border-0">
                  <span className="min-w-0 flex-1 truncate text-ink">{l.to}</span>
                  <Button
                    variant="ghost" size="sm" iconOnly
                    aria-label={`Copiar el enlace de ${l.to}`}
                    onClick={() => {
                      void navigator.clipboard.writeText(l.link);
                      Utils.toast('Enlace copiado', 'success');
                    }}
                  >
                    <i className="bi bi-clipboard" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Drawer>
  );
}

/* ── El seguimiento ────────────────────────────────────────────────────── */

const ESTADOS: Record<string, { tono: 'ok' | 'warn' | 'danger' | 'info' | 'neutral'; texto: string }> = {
  pendiente: { tono: 'warn', texto: 'Pendiente' },
  enviada: { tono: 'info', texto: 'Enviada' },
  devuelta: { tono: 'warn', texto: 'Devuelta' },
  aprobada: { tono: 'ok', texto: 'Aprobada' },
  rechazada: { tono: 'danger', texto: 'Rechazada' },
  aplicada: { tono: 'ok', texto: 'Aplicada' },
};

const vencida = (r: Invitacion) =>
  ['pendiente', 'devuelta'].includes(r.status)
  && !!r.expires_at && new Date(r.expires_at) < new Date();

export function Invitaciones({
  formulario, onRevisar, onCerrar,
}: {
  formulario: Formulario;
  onRevisar: (respuestaId: number) => void;
  onCerrar: () => void;
}) {
  const consulta = useInvitaciones(formulario.id);
  const recordar = useRecordarPendientes();
  const filas = consulta.data ?? [];

  const sinResponder = filas.filter(
    (r) => ['pendiente', 'devuelta'].includes(r.status) && !vencida(r)).length;
  const vencidas = filas.filter(vencida).length;
  const respondidas = filas.filter((r) => r.status !== 'pendiente').length;

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={`Invitaciones: ${formulario.name}`}
      descripcion="A quién se le pidió, quién respondió y qué falta."
      ancho="lg"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cerrar</Button>
          <Button
            variant="secondary"
            loading={recordar.isPending}
            disabled={!sinResponder}
            razonDeshabilitado="No hay nadie pendiente al que recordarle"
            onClick={() => recordar.mutate(formulario.id, {
              onSuccess: (r) => Utils.toast(r.message, 'success'),
              onError: (err) => Utils.toast(
                err instanceof Error ? err.message : 'No se pudo recordar', 'error'),
            })}
          >
            <i className="bi bi-bell" aria-hidden="true" /> Recordar a pendientes
          </Button>
        </>
      }
    >
      {consulta.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="las invitaciones"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !filas.length ? (
        <EstadoVacio
          icono="bi-send"
          titulo="Sin invitaciones"
          descripcion="Usa «Invitar» para repartir el formulario."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Dato n={filas.length} etiqueta="Enviadas" />
            <Dato n={respondidas} etiqueta="Respondidas" />
            <Dato n={sinResponder} etiqueta="Sin responder" />
            {vencidas > 0 && <Dato n={vencidas} etiqueta="Vencidas" />}
          </dl>

          <div className="overflow-auto rounded-sm border border-line">
            <table className="w-full border-collapse text-2">
              <thead className="sticky top-0 bg-sunken">
                <tr className="border-b border-line text-left text-muted">
                  {['Para', 'Tipo', 'Estado', 'Enviado', ''].map((h, i) => (
                    <th key={i} scope="col" className="whitespace-nowrap px-3 py-2 font-semibold">
                      {h || <span className="sr-only">Acciones</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.map((r) => {
                  const e = vencida(r)
                    ? { tono: 'neutral' as const, texto: 'Vencida' }
                    : ESTADOS[r.status] ?? { tono: 'neutral' as const, texto: r.status };
                  return (
                    <tr key={r.id} className="border-b border-line last:border-0">
                      <td className="px-3 py-1.5 text-ink">{r.to}</td>
                      <td className="px-3 py-1.5 text-muted">
                        {r.is_new_person ? 'Nuevo/externo' : 'Colaborador'}
                      </td>
                      <td className="px-3 py-1.5"><Badge tone={e.tono}>{e.texto}</Badge></td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-muted">
                        {fechaHora(r.submitted_at)}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {r.status === 'enviada' && (
                          <Button size="sm" onClick={() => onRevisar(r.id)}>
                            <i className="bi bi-eye" aria-hidden="true" /> Revisar
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function Dato({ n, etiqueta }: { n: number; etiqueta: string }) {
  return (
    <div className="rounded-sm border border-line bg-sunken p-2 text-center">
      <dt className="sr-only">{etiqueta}</dt>
      <dd>
        <strong className="block text-6 font-extrabold leading-none tabular-nums text-brand-ink">{n}</strong>
        <span className="mt-1 block text-1 text-muted" aria-hidden="true">{etiqueta}</span>
      </dd>
    </div>
  );
}
